import { forEach } from 'lodash';
import { Observer } from 'rxjs';
import service from 'src/ServiceContainer';
import { AssortmentEventType, EventSourceContext, ServerSentEventType } from './ServerSentEventSource.types';
import { API_BASE_URL } from 'src/state/ViewConfig/ViewConfig.types';
import { planTrackerLens } from '../lenses/lenses';
import { update } from '../lenses/Lenses.actions';
import store from 'src/store';

export class ServerSentEventSource {
  private context: EventSourceContext = 'assortment'; // default to assortment for now
  private observer: Observer<Event>;
  private eventOpened = false;
  private eventSource: EventSource | undefined;
  private expectedEventTypes: Set<ServerSentEventType> = new Set();
  private receivedEventTypes = new Set<string>();
  private noEventsConnectionTimeout: NodeJS.Timeout | undefined = undefined;
  private missingEventTypesTimeout: NodeJS.Timeout | undefined = undefined;
  accessToken: string;
  eventTypes: ServerSentEventType[];

  // #region retry vars
  private latestEventTime = new Date();
  private MAX_EVENT_GAP_SECONDS = 30;
  private MAX_RETRY_GAP_MS = 45000; // 45 seconds
  private eventTestTimeout: NodeJS.Timeout | undefined = undefined;
  private retryCnt = 0;
  // #endregion

  private initConnection() {
    const sourceUrl = this.context === 'assortment' ? '/asst/api/bus/events' : `${API_BASE_URL}/events`;
    this.eventSource = new EventSource(`${sourceUrl}?token=${encodeURIComponent(this.accessToken)}`);
    this.expectedEventTypes = new Set<ServerSentEventType>(this.eventTypes);

    // setup event listeners
    this.eventSource.addEventListener('open', this.onOpen, false);
    this.eventSource.addEventListener('error', this.onError, false);
    this.expectedEventTypes.forEach((et) => {
      this.eventSource?.addEventListener(et.type, this.onMessage, false);
    });

    // start a timer to handle no connection after 5 seconds
    this.noEventsConnectionTimeout = setTimeout(() => this.onNoEventsConnection(), 5000);
  }

  constructor(
    context: EventSourceContext,
    observer: Observer<Event>,
    accessToken: string,
    eventTypes: ServerSentEventType[],
  ) {
    this.context = context;
    this.observer = observer;
    this.accessToken = accessToken;
    this.eventTypes = eventTypes;
    this.initConnection();
  }

  private dispatchEventFailed = (type: AssortmentEventType) => {
    switch (type) {
      case AssortmentEventType.planQueue:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, assortmentPlanError: true })),
            'ASST_PLAN_ERRORED',
          ),
        );
        break;
      case AssortmentEventType.allocPlanQueue:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, allocationPlanError: true })),
            'ALLOC_PLAN_ERRORED',
          ),
        );
        break;
      case AssortmentEventType.add_to_assortment:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, addToAsstError: true })),
            'ATA_ERROR',
          ),
        );
        break;
      default:
        // ignore others or add more mappings here as needed
        break;
    }
  };

  private dispatchEventSuccess = (event: { type: AssortmentEventType }) => {
    switch (event.type) {
      case AssortmentEventType.planQueue:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, assortmentPlanError: false })),
            'ASST_PLAN_QUEUE_SUCCESS',
          ),
        );
        break;
      case AssortmentEventType.allocPlanQueue:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, allocationPlanError: false })),
            'ALLOC_PLAN_QUEUE_SUCCESS',
          ),
        );
        break;
      case AssortmentEventType.add_to_assortment:
        store.dispatch(
          update(
            planTrackerLens.modify((p) => ({ ...p, addToAsstError: false })),
            'ATA_SUCCESS',
          ),
        );
        break;
    }
  };

  private dispatchAsstAllFailed = () => {
    this.dispatchEventFailed(AssortmentEventType.planQueue);
    this.dispatchEventFailed(AssortmentEventType.allocPlanQueue);
    this.dispatchEventFailed(AssortmentEventType.add_to_assortment);
  };

  private onOpen = () => {
    // clear retry counter on successful open connection

    this.eventOpened = true;
    // start a timer to log missing events after 10 seconds
    this.missingEventTypesTimeout = setTimeout(() => this.onMissingEventTypes(), 10000);
    // check every second to see if we surpassed our event gap limit. If we do, trigger error to
    // allow for reconnect retry.
    this.eventTestTimeout = setInterval(() => {
      const now = new Date();
      const maxEventGapMs = 1000 * this.MAX_EVENT_GAP_SECONDS;
      if (now.getTime() > this.latestEventTime.getTime() + maxEventGapMs) {
        this.onError(new Event(`No events received in ${this.MAX_EVENT_GAP_SECONDS} seconds.`));
      }
    }, 1000);
  };

  private onMessage = (e: Event) => {
    // store message type for validation that all messages are being received
    const msgType = e.type;
    this.retryCnt = 0;
    this.latestEventTime = new Date();
    this.receivedEventTypes.add(msgType);
    this.dispatchEventSuccess({ type: msgType as AssortmentEventType });
    this.observer.next(e);
  };

  private onError = (_err?: Event) => {
    this.cleanup();
    if (this.context == 'assortment') {
      this.dispatchAsstAllFailed();
    }
    // Retry on error
    // Put some random space in the retry to prevent every connected client hitting at same time
    const retryFuzz = Math.floor(Math.random() * 10) * 100;
    this.retryCnt += 1;
    const exponentialRetryGap = Math.pow(2, this.retryCnt);
    const fullRetryGap = 100 * exponentialRetryGap + retryFuzz;
    // default retry gap to MAX_RETRY_GAP_MS if fullRetryGap exceeds MAX_RETRY_GAP_MS value
    const maxGap = Math.min(fullRetryGap, this.MAX_RETRY_GAP_MS);

    setTimeout(() => {
      this.initConnection();
    }, maxGap);
  };

  private onNoEventsConnection = () => {
    if (!this.eventOpened) {
      // trigger failure state here
      // TODO: want to construct useful event here for logging
      // const err = new ServerSentEventConnectionError(this.context);
      this.onError();
    }

    clearTimeout(this.noEventsConnectionTimeout);
  };

  private onMissingEventTypes = () => {
    // only logs for missing events that are required
    const missingEventTypes: string[] = Array.from(this.expectedEventTypes)
      .filter((et) => et.required && !this.receivedEventTypes.has(et.type))
      .map((et) => et.type);

    if (missingEventTypes.length > 0) {
      const eventList = missingEventTypes.join(', ');
      service.loggingService.warn(`Missing event types from ${this.context} event source: ${eventList}`);
      missingEventTypes.forEach((msgType) => {
        this.dispatchEventFailed(msgType as AssortmentEventType);
      });
    }

    clearTimeout(this.missingEventTypesTimeout);
  };

  private cleanup = () => {
    service.loggingService.warn(`Cleaning up all ${this.context} ServerSentEventSource listeners`);

    // this is precautionary in case an error occurs before these
    // event handlers are completed and can clean up their own timeouts
    clearTimeout(this.noEventsConnectionTimeout);
    clearTimeout(this.missingEventTypesTimeout);
    clearTimeout(this.eventTestTimeout);

    forEach(Array.from(this.receivedEventTypes), (msgType) => {
      this.eventSource?.removeEventListener(msgType, this.onMessage);
    });

    this.eventSource?.removeEventListener('error', this.onError, false);
    this.eventSource?.removeEventListener('open', this.onOpen, false);
    this.eventSource?.close();
  };

  public unsubscribe = () => {
    this.cleanup();
  };
}
